'use client';

import { useGSAP } from '@gsap/react';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import clsx from 'clsx';
import { FC, useRef } from 'react';

import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap, ScrollTrigger, SplitText } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: {
		title?: BlocksContent;
		text?: BlocksContent;
	};
	className?: string;
}

const HeadingBlock: FC<Props> = ({ data, className }) => {
	const { title, text } = data;
	const wrapperRef = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLDivElement>(null);
	const textRef = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const wrapper = wrapperRef.current;
			if (!wrapper) return;

			const splitInstances: SplitText[] = [];
			const lineInners: HTMLElement[] = [];

			const prepareLines = (element: HTMLElement | null) => {
				if (!element) return;

				const split = new SplitText(element, {
					type: 'lines',
					linesClass: css.line
				});

				splitInstances.push(split);

				split.lines.forEach(line => {
					const lineEl = line as HTMLElement;

					const inner = document.createElement('span');
					inner.className = css.lineInner;

					while (lineEl.firstChild) inner.appendChild(lineEl.firstChild);

					lineEl.appendChild(inner);
					lineInners.push(inner);
				});
			};

			prepareLines(titleRef.current);
			prepareLines(textRef.current);

			if (!lineInners.length) return;

			gsap.set(lineInners, {
				clipPath: 'inset(0 100% 0 0)'
			});

			const mm = gsap.matchMedia();

			mm.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				() => {
					const linesCount = lineInners.length;
					const perLineRange = 1 / Math.max(3, linesCount);
					const overlap = 0.2;
					const effectiveRange = perLineRange + overlap * perLineRange;

					const tl = gsap.timeline({
						scrollTrigger: {
							trigger: wrapper,
							start: 'top bottom',
							end: 'bottom+=100% top',
							scrub: 0.6,
							invalidateOnRefresh: true,
							onUpdate: ({ progress }) => {
								const normalized = gsap.utils.clamp(0, 1, progress / 0.45);

								lineInners.forEach((el, i) => {
									const center = linesCount === 1 ? 0.5 : i / Math.max(1, linesCount - 1);

									const start = Math.max(0, center - effectiveRange / 2);
									const end = Math.min(1, center + effectiveRange / 2);

									let localProg = 0;

									if (normalized <= start) localProg = 0;
									else if (normalized >= end) localProg = 1;
									else localProg = (normalized - start) / (end - start);

									const hiddenPercent = Math.round((1 - localProg) * 10000) / 100;

									gsap.set(el, {
										clipPath: `inset(0 ${hiddenPercent}% 0 0)`
									});
								});
							}
						}
					});

					return () => {
						tl.scrollTrigger?.kill();
						tl.kill();
					};
				}
			);

			document.fonts?.ready?.then(() => {
				ScrollTrigger.refresh();
			});

			return () => {
				mm.revert();
				splitInstances.forEach(split => split.revert());
			};
		},
		{ scope: wrapperRef }
	);

	return (
		<section ref={wrapperRef} className={clsx(css.section, className)}>
			{title ? (
				<div ref={titleRef} className={clsx(css.title, css.uppercase)}>
					<StrapiRichContent content={title} />
				</div>
			) : null}
			{text ? (
				<div ref={textRef} className={css.text}>
					<StrapiRichContent content={text} />
				</div>
			) : null}
		</section>
	);
};

export default HeadingBlock;
